import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useDemo } from './DemoContext';
import API from '@/utils/api';

/**
 * EntitlementContext - Centralized subscription tier and feature access control
 * 
 * This context provides the single source of truth for:
 * - User's current subscription tier (FREE, PREMIUM, FAMILY)
 * - Feature entitlements and access checks
 * - Upgrade prompts and tier requirements
 * 
 * DESIGN PRINCIPLES:
 * 1. All entitlement checks go through this context
 * 2. Demo mode (Carter Family) bypasses all entitlement checks
 * 3. Backend validates all protected endpoints (frontend gating is UX only)
 * 4. Expired subscriptions make data read-only, not deleted
 */

// Subscription tiers in order of increasing access
export const SUBSCRIPTION_TIERS = {
  FREE: 'free',
  PREMIUM: 'premium',
  FAMILY: 'family',
};

// Tier hierarchy for comparison
const TIER_HIERARCHY = {
  [SUBSCRIPTION_TIERS.FREE]: 0,
  [SUBSCRIPTION_TIERS.PREMIUM]: 1,
  [SUBSCRIPTION_TIERS.FAMILY]: 2,
};

// Pricing info for upgrade prompts
export const TIER_PRICING = {
  [SUBSCRIPTION_TIERS.FREE]: { price: 0, interval: null },
  [SUBSCRIPTION_TIERS.PREMIUM]: { price: 2.99, interval: 'month' },
  [SUBSCRIPTION_TIERS.FAMILY]: { price: 5.99, interval: 'month' },
};

// Feature categories for organization
export const FEATURE_CATEGORIES = {
  PROFILES: 'profiles',
  MEDICAL: 'medical',
  DOCUMENTS: 'documents',
  SHARING: 'sharing',
  FAMILY: 'family',
  VEHICLES: 'vehicles',
  REMINDERS: 'reminders',
  SECURITY: 'security',
  ORGANIZATION: 'organization',
};

/**
 * Client-side feature entitlement matrix
 * This mirrors the backend entitlement_service.py
 * Used for instant UI gating (backend still validates)
 */
export const FEATURE_ENTITLEMENTS = {
  // =========== FREE TIER FEATURES ===========
  basic_profile: {
    tier: SUBSCRIPTION_TIERS.FREE,
    category: FEATURE_CATEGORIES.PROFILES,
    name: 'Basic Profile',
    description: 'One personal emergency profile',
    limit: 1,
  },
  basic_emergency_info: {
    tier: SUBSCRIPTION_TIERS.FREE,
    category: FEATURE_CATEGORIES.MEDICAL,
    name: 'Basic Emergency Info',
    description: 'Essential emergency information',
  },
  limited_emergency_contacts: {
    tier: SUBSCRIPTION_TIERS.FREE,
    category: FEATURE_CATEGORIES.MEDICAL,
    name: 'Emergency Contacts',
    description: 'Store emergency contacts',
    limit: 3,
  },
  basic_allergies: {
    tier: SUBSCRIPTION_TIERS.FREE,
    category: FEATURE_CATEGORIES.MEDICAL,
    name: 'Allergy Tracking',
    description: 'Record allergies and reactions',
    limit: 5,
  },
  basic_medications: {
    tier: SUBSCRIPTION_TIERS.FREE,
    category: FEATURE_CATEGORIES.MEDICAL,
    name: 'Medication List',
    description: 'Track current medications',
    limit: 5,
  },
  basic_reminders: {
    tier: SUBSCRIPTION_TIERS.FREE,
    category: FEATURE_CATEGORIES.REMINDERS,
    name: 'Basic Reminders',
    description: 'Limited reminder functionality',
    limit: 5,
  },
  basic_parking: {
    tier: SUBSCRIPTION_TIERS.FREE,
    category: FEATURE_CATEGORIES.ORGANIZATION,
    name: 'Parking Location',
    description: 'Save where you parked',
    limit: 1,
  },
  demo_access: {
    tier: SUBSCRIPTION_TIERS.FREE,
    category: FEATURE_CATEGORIES.PROFILES,
    name: 'Demo Mode',
    description: 'Explore with Carter Family demo',
  },

  // =========== PREMIUM TIER FEATURES ===========
  unlimited_records: {
    tier: SUBSCRIPTION_TIERS.PREMIUM,
    category: FEATURE_CATEGORIES.MEDICAL,
    name: 'Unlimited Records',
    description: 'No limits on medical records',
    upgradePrompt: 'Upgrade to Premium for unlimited medical records',
  },
  document_storage: {
    tier: SUBSCRIPTION_TIERS.PREMIUM,
    category: FEATURE_CATEGORIES.DOCUMENTS,
    name: 'Document Storage',
    description: 'Store important documents securely',
    upgradePrompt: 'Upgrade to Premium to store important documents',
  },
  insurance_scanning: {
    tier: SUBSCRIPTION_TIERS.PREMIUM,
    category: FEATURE_CATEGORIES.DOCUMENTS,
    name: 'Insurance Card Scanning',
    description: 'Scan and OCR insurance cards',
    upgradePrompt: 'Upgrade to Premium to scan insurance cards',
  },
  doctor_summary: {
    tier: SUBSCRIPTION_TIERS.PREMIUM,
    category: FEATURE_CATEGORIES.MEDICAL,
    name: 'Doctor Summary',
    description: 'Comprehensive doctor summary view',
    upgradePrompt: 'Upgrade to Premium for Doctor Summary',
  },
  pdf_export: {
    tier: SUBSCRIPTION_TIERS.PREMIUM,
    category: FEATURE_CATEGORIES.SHARING,
    name: 'PDF Export',
    description: 'Export binder as PDF',
    upgradePrompt: 'Upgrade to Premium to export PDFs',
  },
  print_binder: {
    tier: SUBSCRIPTION_TIERS.PREMIUM,
    category: FEATURE_CATEGORIES.SHARING,
    name: 'Print Binder',
    description: 'Print your emergency binder',
    upgradePrompt: 'Upgrade to Premium to print your binder',
  },
  medical_history: {
    tier: SUBSCRIPTION_TIERS.PREMIUM,
    category: FEATURE_CATEGORIES.MEDICAL,
    name: 'Medical History Tools',
    description: 'Conditions, surgeries, and medical history',
    upgradePrompt: 'Upgrade to Premium for medical history tracking',
  },
  vehicle_records: {
    tier: SUBSCRIPTION_TIERS.PREMIUM,
    category: FEATURE_CATEGORIES.VEHICLES,
    name: 'Vehicle Records',
    description: 'Full vehicle tracking and maintenance',
    upgradePrompt: 'Upgrade to Premium for vehicle records',
  },
  vin_lookup: {
    tier: SUBSCRIPTION_TIERS.PREMIUM,
    category: FEATURE_CATEGORIES.VEHICLES,
    name: 'VIN Lookup',
    description: 'Decode vehicle information from VIN',
    upgradePrompt: 'Upgrade to Premium for VIN lookup',
  },
  advanced_reminders: {
    tier: SUBSCRIPTION_TIERS.PREMIUM,
    category: FEATURE_CATEGORIES.REMINDERS,
    name: 'Advanced Reminders',
    description: 'Unlimited reminders with recurring options',
    upgradePrompt: 'Upgrade to Premium for advanced reminders',
  },
  app_lock: {
    tier: SUBSCRIPTION_TIERS.PREMIUM,
    category: FEATURE_CATEGORIES.SECURITY,
    name: 'App Lock',
    description: 'Biometric and passcode protection',
    upgradePrompt: 'Upgrade to Premium for enhanced security',
  },
  financial_overview: {
    tier: SUBSCRIPTION_TIERS.PREMIUM,
    category: FEATURE_CATEGORIES.ORGANIZATION,
    name: 'Financial Overview',
    description: 'Track financial accounts and contacts',
    upgradePrompt: 'Upgrade to Premium for financial organization',
  },
  end_of_life_planning: {
    tier: SUBSCRIPTION_TIERS.PREMIUM,
    category: FEATURE_CATEGORIES.ORGANIZATION,
    name: 'End-of-Life Planning',
    description: 'Will, directives, and legacy planning',
    upgradePrompt: 'Upgrade to Premium for end-of-life planning',
  },
  household_info: {
    tier: SUBSCRIPTION_TIERS.PREMIUM,
    category: FEATURE_CATEGORIES.ORGANIZATION,
    name: 'Household Information',
    description: 'WiFi, codes, utilities, and home details',
    upgradePrompt: 'Upgrade to Premium for household info',
  },
  pet_info: {
    tier: SUBSCRIPTION_TIERS.PREMIUM,
    category: FEATURE_CATEGORIES.ORGANIZATION,
    name: 'Pet Information',
    description: 'Pet profiles, vets, and care instructions',
    upgradePrompt: 'Upgrade to Premium for pet tracking',
  },

  // =========== FAMILY TIER FEATURES ===========
  multiple_profiles: {
    tier: SUBSCRIPTION_TIERS.FAMILY,
    category: FEATURE_CATEGORIES.FAMILY,
    name: 'Multiple Profiles',
    description: 'Create profiles for family members',
    limit: 10,
    upgradePrompt: 'Upgrade to Family for multiple profiles',
  },
  family_network: {
    tier: SUBSCRIPTION_TIERS.FAMILY,
    category: FEATURE_CATEGORIES.FAMILY,
    name: 'Family Network',
    description: 'Connect and share with family members',
    upgradePrompt: 'Upgrade to Family to connect your family network',
  },
  shared_household: {
    tier: SUBSCRIPTION_TIERS.FAMILY,
    category: FEATURE_CATEGORIES.FAMILY,
    name: 'Shared Household',
    description: 'Share household information with family',
    upgradePrompt: 'Upgrade to Family for shared household features',
  },
  caregiver_access: {
    tier: SUBSCRIPTION_TIERS.FAMILY,
    category: FEATURE_CATEGORIES.FAMILY,
    name: 'Caregiver Access',
    description: 'Grant caregivers read-only access',
    upgradePrompt: 'Upgrade to Family to add caregivers',
  },
  emergency_sharing: {
    tier: SUBSCRIPTION_TIERS.FAMILY,
    category: FEATURE_CATEGORIES.FAMILY,
    name: 'Controlled Emergency Sharing',
    description: 'Share emergency info with QR codes',
    upgradePrompt: 'Upgrade to Family for emergency sharing',
  },
  cross_account_viewing: {
    tier: SUBSCRIPTION_TIERS.FAMILY,
    category: FEATURE_CATEGORIES.FAMILY,
    name: 'Cross-Account Viewing',
    description: "View linked family members' information",
    upgradePrompt: 'Upgrade to Family for cross-account access',
  },
  family_calendar: {
    tier: SUBSCRIPTION_TIERS.FAMILY,
    category: FEATURE_CATEGORIES.FAMILY,
    name: 'Family Calendar',
    description: 'Shared family events and reminders',
    upgradePrompt: 'Upgrade to Family for shared calendar',
  },
  family_sharing: {
    tier: SUBSCRIPTION_TIERS.FAMILY,
    category: FEATURE_CATEGORIES.SHARING,
    name: 'Family Sharing Controls',
    description: "Control what's shared with family",
    upgradePrompt: 'Upgrade to Family for sharing controls',
  },
};

const EntitlementContext = createContext(null);

export const useEntitlement = () => {
  const context = useContext(EntitlementContext);
  if (!context) {
    throw new Error('useEntitlement must be used within an EntitlementProvider');
  }
  return context;
};

export const EntitlementProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const { isDemoMode } = useDemo();
  
  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user entitlements from backend
  const fetchEntitlements = useCallback(async () => {
    // Demo mode gets full access
    if (isDemoMode) {
      setEntitlements({
        tier: SUBSCRIPTION_TIERS.FAMILY,
        isActive: true,
        isDemo: true,
        features: Object.keys(FEATURE_ENTITLEMENTS),
        limits: {},
      });
      setLoading(false);
      return;
    }

    // Not authenticated = free tier
    if (!isAuthenticated) {
      setEntitlements({
        tier: SUBSCRIPTION_TIERS.FREE,
        isActive: false,
        isDemo: false,
        features: Object.keys(FEATURE_ENTITLEMENTS).filter(
          key => FEATURE_ENTITLEMENTS[key].tier === SUBSCRIPTION_TIERS.FREE
        ),
        limits: {
          basic_profile: 1,
          limited_emergency_contacts: 3,
          basic_allergies: 5,
          basic_medications: 5,
          basic_reminders: 5,
          basic_parking: 1,
        },
      });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await API.get('/subscription/entitlements');
      setEntitlements({
        ...response.data,
        isDemo: false,
      });
      setError(null);
    } catch (err) {
      console.error('Failed to fetch entitlements:', err);
      // Default to free tier on error
      setEntitlements({
        tier: SUBSCRIPTION_TIERS.FREE,
        isActive: false,
        isDemo: false,
        features: Object.keys(FEATURE_ENTITLEMENTS).filter(
          key => FEATURE_ENTITLEMENTS[key].tier === SUBSCRIPTION_TIERS.FREE
        ),
        limits: {},
      });
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, isDemoMode]);

  // Fetch entitlements on auth/demo state change
  useEffect(() => {
    fetchEntitlements();
  }, [fetchEntitlements]);

  // Get user's current tier
  const currentTier = useMemo(() => {
    return entitlements?.tier || SUBSCRIPTION_TIERS.FREE;
  }, [entitlements]);

  // Check if user's tier includes access to required tier
  const tierIncludes = useCallback((requiredTier) => {
    if (isDemoMode) return true;
    const userLevel = TIER_HIERARCHY[currentTier] || 0;
    const requiredLevel = TIER_HIERARCHY[requiredTier] || 0;
    return userLevel >= requiredLevel;
  }, [currentTier, isDemoMode]);

  /**
   * Check if user has access to a specific feature
   * 
   * @param {string} featureKey - The feature key from FEATURE_ENTITLEMENTS
   * @returns {{ allowed: boolean, feature: object, requiredTier: string, upgradePrompt: string, limit: number|null }}
   */
  const checkFeatureAccess = useCallback((featureKey) => {
    // Demo mode has access to everything
    if (isDemoMode) {
      const feature = FEATURE_ENTITLEMENTS[featureKey] || {};
      return {
        allowed: true,
        feature,
        isDemo: true,
        limit: null, // No limits in demo
      };
    }

    const feature = FEATURE_ENTITLEMENTS[featureKey];
    if (!feature) {
      return {
        allowed: false,
        feature: null,
        error: `Unknown feature: ${featureKey}`,
      };
    }

    const requiredTier = feature.tier || SUBSCRIPTION_TIERS.FREE;
    const allowed = tierIncludes(requiredTier);

    const result = {
      allowed,
      feature,
      requiredTier,
      userTier: currentTier,
    };

    if (allowed) {
      // Include limit for free tier users
      if (currentTier === SUBSCRIPTION_TIERS.FREE && feature.limit) {
        result.limit = feature.limit;
      } else {
        result.limit = null; // Unlimited for paid tiers
      }
    } else {
      result.upgradePrompt = feature.upgradePrompt || `Upgrade to ${requiredTier} to access this feature`;
      result.requiredPlan = requiredTier === SUBSCRIPTION_TIERS.PREMIUM ? 'premium' : 'family';
    }

    return result;
  }, [currentTier, isDemoMode, tierIncludes]);

  /**
   * Get all features available at user's current tier
   */
  const availableFeatures = useMemo(() => {
    if (isDemoMode) {
      return Object.entries(FEATURE_ENTITLEMENTS).map(([key, feature]) => ({
        key,
        ...feature,
      }));
    }

    return Object.entries(FEATURE_ENTITLEMENTS)
      .filter(([_, feature]) => tierIncludes(feature.tier))
      .map(([key, feature]) => ({
        key,
        ...feature,
      }));
  }, [isDemoMode, tierIncludes]);

  /**
   * Get features that require upgrade
   */
  const lockedFeatures = useMemo(() => {
    if (isDemoMode) return [];

    return Object.entries(FEATURE_ENTITLEMENTS)
      .filter(([_, feature]) => !tierIncludes(feature.tier))
      .map(([key, feature]) => ({
        key,
        ...feature,
      }));
  }, [isDemoMode, tierIncludes]);

  /**
   * Check if user's subscription is expired
   * Expired users have read-only access to their data
   */
  const isExpired = useMemo(() => {
    return entitlements?.is_expired || false;
  }, [entitlements]);

  /**
   * Check if data should be read-only (expired subscription)
   */
  const isReadOnly = useMemo(() => {
    if (isDemoMode) return false;
    return isExpired;
  }, [isDemoMode, isExpired]);

  /**
   * Get tier display info for UI
   */
  const getTierInfo = useCallback((tier) => {
    const pricing = TIER_PRICING[tier] || TIER_PRICING[SUBSCRIPTION_TIERS.FREE];
    const tierNames = {
      [SUBSCRIPTION_TIERS.FREE]: 'Free',
      [SUBSCRIPTION_TIERS.PREMIUM]: 'Premium',
      [SUBSCRIPTION_TIERS.FAMILY]: 'Family',
    };
    const tierColors = {
      [SUBSCRIPTION_TIERS.FREE]: 'slate',
      [SUBSCRIPTION_TIERS.PREMIUM]: 'teal',
      [SUBSCRIPTION_TIERS.FAMILY]: 'purple',
    };
    const tierIcons = {
      [SUBSCRIPTION_TIERS.FREE]: 'User',
      [SUBSCRIPTION_TIERS.PREMIUM]: 'Crown',
      [SUBSCRIPTION_TIERS.FAMILY]: 'Users',
    };

    return {
      name: tierNames[tier] || 'Free',
      color: tierColors[tier] || 'slate',
      icon: tierIcons[tier] || 'User',
      price: pricing.price,
      interval: pricing.interval,
      priceDisplay: pricing.price > 0 ? `$${pricing.price}/${pricing.interval}` : 'Free',
    };
  }, []);

  // Refresh entitlements (e.g., after purchase)
  const refreshEntitlements = useCallback(() => {
    return fetchEntitlements();
  }, [fetchEntitlements]);

  const value = {
    // State
    entitlements,
    loading,
    error,
    
    // Current tier info
    currentTier,
    isExpired,
    isReadOnly,
    isDemoMode,
    
    // Feature access
    checkFeatureAccess,
    tierIncludes,
    availableFeatures,
    lockedFeatures,
    
    // Helpers
    getTierInfo,
    refreshEntitlements,
    
    // Constants (for convenience)
    TIERS: SUBSCRIPTION_TIERS,
    FEATURES: FEATURE_ENTITLEMENTS,
    CATEGORIES: FEATURE_CATEGORIES,
  };

  return (
    <EntitlementContext.Provider value={value}>
      {children}
    </EntitlementContext.Provider>
  );
};

export default EntitlementContext;
